import {
  API_KEY,
  BRANCH,
  DELIVERY_API_URL,
  DELIVERY_TOKEN,
  ENVIRONMENT,
} from '../setup/settings';
import { isPlainObject, PlainObject } from '../helpers/helpers';

type FieldPath = (string | number)[];

interface MixedUpRichText {
  entry: PlainObject;
  contentTypeUid: string;
  path: FieldPath;
}

const isRichTextDocument = (value: unknown): boolean =>
  isPlainObject(value) &&
  value.type === 'doc' &&
  typeof value.uid === 'string' &&
  typeof value._version === 'number';

const isReferencedEntry = (value: unknown): boolean =>
  isPlainObject(value) &&
  typeof value.uid === 'string' &&
  typeof value._version === 'number' &&
  typeof value._content_type_uid === 'string';

const findMixedUpRichText = (
  rootEntry: PlainObject,
  rootContentTypeUid: string
): MixedUpRichText[] => {
  const mixedUp: MixedUpRichText[] = [];
  const checkedEntries = new WeakSet<PlainObject>();
  const entriesToCheck: [PlainObject, string][] = [
    [rootEntry, rootContentTypeUid],
  ];

  while (entriesToCheck.length) {
    const [entry, contentTypeUid] = entriesToCheck.shift() as [
      PlainObject,
      string,
    ];

    if (checkedEntries.has(entry)) continue;

    checkedEntries.add(entry);

    const checkField = (value: unknown, path: FieldPath): void => {
      if (Array.isArray(value)) {
        value.forEach((item, index) => checkField(item, [...path, index]));

        return;
      }

      if (!isPlainObject(value)) return;

      if (isRichTextDocument(value)) {
        if (value._version !== entry._version) {
          mixedUp.push({ entry, contentTypeUid, path });
        }

        return;
      }

      if (isReferencedEntry(value)) {
        entriesToCheck.push([value, value._content_type_uid]);

        return;
      }

      Object.entries(value).forEach(([fieldName, fieldValue]) =>
        checkField(fieldValue, [...path, fieldName])
      );
    };

    if (typeof entry._version === 'number') {
      Object.entries(entry).forEach(([fieldName, value]) =>
        checkField(value, [fieldName])
      );
    }
  }

  return mixedUp;
};

const fetchPublishedEntry = async (
  contentTypeUid: string,
  entryUid: string,
  locale?: string
): Promise<PlainObject | null> => {
  const params = new URLSearchParams({ environment: ENVIRONMENT });

  if (locale) params.set('locale', locale);

  try {
    const response = await fetch(
      `${DELIVERY_API_URL}/content_types/${contentTypeUid}/entries/${entryUid}?${params}`,
      {
        headers: {
          api_key: API_KEY,
          access_token: DELIVERY_TOKEN,
          branch: BRANCH,
        },
      }
    );

    return response.ok ? ((await response.json()).entry ?? null) : null;
  } catch {
    return null;
  }
};

const getValueAtPath = (source: unknown, path: FieldPath): PlainObject =>
  path.reduce<PlainObject>(
    (current, key) => current?.[key],
    source as PlainObject
  );

export const repairMixedUpRichText = async (
  rootEntry: PlainObject,
  rootContentTypeUid: string,
  locale?: string
): Promise<void> => {
  const mixedUpFields = findMixedUpRichText(rootEntry, rootContentTypeUid);

  if (!mixedUpFields.length) return;

  const publishedEntryRequests = new Map<
    PlainObject,
    Promise<PlainObject | null>
  >();

  await Promise.all(
    mixedUpFields.map(async ({ entry, contentTypeUid, path }) => {
      if (!publishedEntryRequests.has(entry)) {
        publishedEntryRequests.set(
          entry,
          fetchPublishedEntry(contentTypeUid, entry.uid, locale)
        );
      }

      const publishedEntry = await publishedEntryRequests.get(entry);
      const publishedRichText = getValueAtPath(publishedEntry, path);
      const hasPublishedCopy = isRichTextDocument(publishedRichText);
      const parentField = getValueAtPath(entry, path.slice(0, -1));

      parentField[path[path.length - 1]] = hasPublishedCopy
        ? publishedRichText
        : null;

      console.warn(
        `[live-preview] ${contentTypeUid} ${entry.uid}: preview API returned another entry's rich text at ${path.join('.')} - ${
          hasPublishedCopy ? 'showing the published copy' : 'left empty'
        }`
      );
    })
  );
};
